import find from 'lodash/find';
import { Action } from 'redux';

import { Edge } from 'modules/workflows/types';
import { WorkflowType } from '../../enums';
import { parseEdge, updateEdgeDeleteStatus } from '../../utils';
import {
  AddEdgeAction,
  AddEdgesAction,
  CloneEdgesAction,
  CreateWorkflowDraftResponseAction,
  DeleteEdgeAction,
  DeleteEdgesAction,
  DeleteNodeAction,
  DeleteNodesAction,
  FetchWorkflowResponseAction,
  ImportTilesetAction,
  ReassignNodeIdAction,
  SaveWorkflowDraftResponseAction,
  JourneyVisitorMetricsResponseAction,
  actionTypes as t,
  WorkflowViewAction,
} from '../actions';
import { WorkflowState } from '../ducks.types';
import { cloneEdges, updatePercentageOnEdges, updateEdgesOnWorkflowViewChange } from './utils';

export const edgesReducer = (state: Edge[] = [], action: Action, wfState: WorkflowState) => {
  switch (action.type) {
    case t.CREATE_WORKFLOW_DRAFT_RESPONSE:
    case t.SAVE_WORKFLOW_DRAFT_RESPONSE: {
      const { payload } = action as
        | CreateWorkflowDraftResponseAction
        | SaveWorkflowDraftResponseAction;
      return (payload.workflow?.edges || []).map(parseEdge);
    }
    case t.FETCH_WORKFLOW_RESPONSE: {
      const { payload } = action as FetchWorkflowResponseAction;
      const { workflowType } = payload.workflow;
      const isPublishedDraft = workflowType === WorkflowType.PUBLISHED;

      const parsedEdges = (payload.workflow?.edges || []).map(parseEdge);

      if (!isPublishedDraft) return parsedEdges;

      // if the journey is published, then do not enable deletion of edge
      return parsedEdges.map((edge) => updateEdgeDeleteStatus(edge, !isPublishedDraft));
    }
    case t.ADD_EDGE: {
      const { edge = {} } = (action as AddEdgeAction).payload;
      return [...state, { ...edge }];
    }
    case t.ADD_EDGES: {
      const { edges = [] } = (action as AddEdgesAction).payload;
      return [...state, ...edges];
    }
    case t.CLONE_EDGES: {
      return cloneEdges(state, action as CloneEdgesAction, wfState);
    }
    case t.DELETE_EDGE: {
      const { edge } = (action as DeleteEdgeAction).payload;
      return state.filter(
        ({ source, target, sourceAnchor }) =>
          edge.source !== source || edge.target !== target || edge.sourceAnchor !== sourceAnchor,
      );
    }
    case t.DELETE_EDGES: {
      const { edges } = (action as DeleteEdgesAction).payload;
      return state.filter(
        ({ source, target, sourceAnchor }) => !find(edges, { source, target, sourceAnchor }),
      );
    }
    case t.REASSIGN_NODE_ID: {
      const { prevNodeId, nodeId } = (action as ReassignNodeIdAction).payload;
      return state.map((edge) => ({
        ...edge,
        source: edge.source === prevNodeId ? `${nodeId}` : edge.source,
        target: edge.target === prevNodeId ? `${nodeId}` : edge.target,
      }));
    }
    case t.DELETE_NODE: {
      const { nodeId } = (action as DeleteNodeAction).payload;

      // Remove any attached edges
      return state.filter(({ source, target }) => ![source, target].includes(nodeId));
    }
    case t.DELETE_NODES: {
      const { nodeIds = [] } = (action as DeleteNodesAction).payload;

      // Remove any attached edges
      return state.filter(
        (e) => !nodeIds.includes(e.source as string) && !nodeIds.includes(e.target as string),
      );
    }
    case t.IMPORT_TILESET: {
      return [...state, ...(action as ImportTilesetAction).payload.edges];
    }
    case t.FETCH_JOURNEY_VISITOR_METRICS_RESPONSE: {
      return updatePercentageOnEdges(state, action as JourneyVisitorMetricsResponseAction, wfState);
    }
    case t.UPDATE_WORKFLOW_VIEW: {
      return updateEdgesOnWorkflowViewChange(state, action as WorkflowViewAction, wfState);
    }
    default: {
      return state;
    }
  }
};
